import { useQuery } from '@tanstack/react-query'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { analyticsApi } from 'src/services/analyticsApi'
import { ChartComment } from 'src/pages/ContentAnalysisDetail/mockData/commentsChartMockData'

// GET /v1_private/analytics/fetchComments — comments collected for the posts
// a campaign mentions (sm.back modules/socialComments.js). Types are written
// by hand from src/schema/swagger/v1_private/analytics/fetchComments.yml.

export type BackendSentiment = 'POS' | 'NEU' | 'NEG'

export interface SocialCommentApiItem {
  author: string
  campaign: string | null
  campaignName: string | null
  campaigns: { _id: string; name: string }[]
  /** The platform's comment id; replies point at it via parentCommentId. */
  commentId: string | null
  depth: number
  id: string
  language: string | null
  manual: boolean
  parentCommentId: string | null
  platform: string
  postUrl: string
  rootCommentId: string | null
  sentiment: BackendSentiment | null
  text: string
  threadId: string | null
  timestamp: string | null
  translated: string | null
}

export interface SocialCommentSentimentBreakdown {
  negative: number
  negativePercentage: number
  neutral: number
  neutralPercentage: number
  positive: number
  positivePercentage: number
}

export interface CampaignCommentsApiResponse {
  data: {
    allSources: { source: string; total: number }[]
    campaigns: { _id: string; name: string; total: number }[]
    comments: SocialCommentApiItem[]
    limit: number
    page: number
    sentimentBreakdown: SocialCommentSentimentBreakdown
    totalComments: number
  }
  success: boolean
}

// What the widgets consume: the Content Analysis comment shape, so
// CommentsDonutChart and CommentItem are reused unchanged, plus the filter
// option lists the backend computed.
export interface CampaignComment extends ChartComment {
  campaignName: string | null
  /** Reply threading (Post Details). Absent on older API responses. */
  commentId?: string | null
  depth?: number
  parentCommentId?: string | null
  threadId: string | null
}

export interface CampaignCommentsData {
  allSources: { source: string; total: number }[]
  campaigns: { _id: string; name: string; total: number }[]
  comments: CampaignComment[]
  limit: number
  page: number
  sentimentBreakdown: SocialCommentSentimentBreakdown
  totalComments: number
}

export type FrontendSentiment = ChartComment['sentiment']

export const mapSentiment = (
  sentiment: BackendSentiment | null | undefined
): FrontendSentiment => {
  if (sentiment === 'POS') return 'positive'
  if (sentiment === 'NEG') return 'negative'
  return 'neutral'
}

export const mapSentimentToBackend = (
  sentiment: FrontendSentiment
): BackendSentiment => {
  if (sentiment === 'positive') return 'POS'
  if (sentiment === 'negative') return 'NEG'
  return 'NEU'
}

export const toChartComment = (
  item: SocialCommentApiItem
): CampaignComment => ({
  author: item.author,
  campaignName: item.campaignName,
  commentId: item.commentId ?? null,
  content: item.text,
  depth: item.depth ?? 0,
  highlightedWords: [],
  id: item.id,
  parentCommentId: item.parentCommentId ?? null,
  platform: item.platform as ChartComment['platform'],
  postId: item.postUrl,
  postUrl: item.postUrl,
  sentiment: mapSentiment(item.sentiment),
  threadId: item.threadId,
  timestamp: item.timestamp || undefined,
})

export interface UseGetCampaignCommentsParams {
  campaign?: string
  // Comma-separated ids; when set, overrides `campaign`.
  campaigns?: string
  fromDate?: string
  limit?: number
  page?: number
  search?: string
  selectedSources?: string
  sentiment?: BackendSentiment
  toDate?: string
}

export const useGetCampaignCommentsQuery = (
  params: UseGetCampaignCommentsParams,
  options?: { enabled?: boolean }
) => {
  return useQuery<CampaignCommentsData>({
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const response = await analyticsApi().get<CampaignCommentsApiResponse>(
        '/v1_private/analytics/fetchComments',
        {
          params: {
            campaign: params.campaigns ? undefined : params.campaign,
            campaigns: params.campaigns,
            fromDate: params.fromDate,
            limit: params.limit,
            page: params.page,
            search: params.search || undefined,
            selectedSources: params.selectedSources || undefined,
            sentiment: params.sentiment,
            toDate: params.toDate,
          },
        }
      )
      const data = response.data.data
      return {
        ...data,
        comments: (data.comments || []).map(toChartComment),
      }
    },
    queryKey: [
      QueryKey.GET_CAMPAIGN_COMMENTS,
      params.campaign,
      params.campaigns,
      params.fromDate,
      params.toDate,
      params.sentiment,
      params.selectedSources,
      params.search,
      params.page,
      params.limit,
    ],
    staleTime: 60 * 1000,
  })
}
